import { useState, useCallback, useEffect } from "react";
import type { Map as MapLibreMap, MapMouseEvent } from "maplibre-gl";
import { useSearch } from "@tanstack/react-router";
import { useDebugMode } from "../../hooks/useDebugMode";
import { useMockLocationStore } from "../../stores/mockLocation";
import { Button, Input, Label } from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faArrowPointer } from "@fortawesome/pro-solid-svg-icons/faArrowPointer";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import type { VisitorSearchParams } from "../../lib/visitor-search";

interface MockLocationPanelProps {
  map: MapLibreMap | null;
}

/**
 * Debug panel for setting mock location coordinates.
 * Only renders in debug mode (localhost or ?debug=true).
 */
export function MockLocationPanel({ map }: MockLocationPanelProps) {
  const { isDebugMode } = useDebugMode();
  const search = useSearch({ strict: false }) as VisitorSearchParams;
  const isMapTab = !search.tab || search.tab === "map";
  const {
    mockLocation,
    isClickMapActive,
    setMockLocation,
    setClickMapActive,
    clearMockLocation,
  } = useMockLocationStore();

  const [isExpanded, setIsExpanded] = useState(false);
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");

  // Sync inputs with mock location
  useEffect(() => {
    if (mockLocation) {
      setLatInput(mockLocation.latitude.toFixed(6));
      setLngInput(mockLocation.longitude.toFixed(6));
    }
  }, [mockLocation]);

  // Handle map click when click mode is active
  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      if (isClickMapActive) {
        setMockLocation({
          latitude: e.lngLat.lat,
          longitude: e.lngLat.lng,
        });
      }
    },
    [isClickMapActive, setMockLocation]
  );

  // Attach/detach click handler
  useEffect(() => {
    if (!map) return;

    if (isClickMapActive) {
      map.on("click", handleMapClick);
      map.getCanvas().style.cursor = "crosshair";
    }

    return () => {
      map.off("click", handleMapClick);
      if (!isClickMapActive) {
        map.getCanvas().style.cursor = "";
      }
    };
  }, [map, isClickMapActive, handleMapClick]);

  // Reset cursor when click mode deactivates
  useEffect(() => {
    if (map && !isClickMapActive) {
      map.getCanvas().style.cursor = "";
    }
  }, [map, isClickMapActive]);

  // Set location from inputs
  const handleSetLocation = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setMockLocation({ latitude: lat, longitude: lng });
    }
  };

  // Toggle click map mode
  const handleToggleClickMap = () => {
    setClickMapActive(!isClickMapActive);
  };

  // Clear location
  const handleClear = () => {
    clearMockLocation();
    setLatInput("");
    setLngInput("");
  };

  if (!isDebugMode) return null;
  if (!isMapTab) return null;

  return (
    <div className="absolute bottom-[calc(9rem+env(safe-area-inset-bottom,0px))] right-4 z-50 bg-background border rounded-lg shadow-lg min-w-[200px] flex flex-col">
      {/* Expanded content (above toggle so panel grows upward) */}
      {isExpanded && (
        <div className="px-3 pt-3 pb-2 space-y-3 order-1">
          {/* Current mock coords */}
          {mockLocation && (
            <div className="text-xs text-muted-foreground bg-muted/30 px-2 py-1 rounded">
              Current: {mockLocation.latitude.toFixed(6)}, {mockLocation.longitude.toFixed(6)}
            </div>
          )}

          {/* Lat/Lng inputs */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="mock-lat" className="text-xs">
                  Latitude
                </Label>
                <Input
                  id="mock-lat"
                  type="number"
                  step="0.000001"
                  value={latInput}
                  onChange={(e) => setLatInput(e.target.value)}
                  placeholder="-90 to 90"
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label htmlFor="mock-lng" className="text-xs">
                  Longitude
                </Label>
                <Input
                  id="mock-lng"
                  type="number"
                  step="0.000001"
                  value={lngInput}
                  onChange={(e) => setLngInput(e.target.value)}
                  placeholder="-180 to 180"
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleSetLocation}
              className="w-full"
            >
              Set Location
            </Button>
          </div>

          {/* Click Map toggle */}
          <Button
            variant={isClickMapActive ? "secondary" : "outline"}
            size="sm"
            onClick={handleToggleClickMap}
            className="w-full"
          >
            <FontAwesomeIcon icon={faArrowPointer} className="h-4 w-4 mr-1" />
            {isClickMapActive ? "Click Map Active..." : "Click Map"}
          </Button>

          {/* Clear button */}
          {mockLocation && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="w-full text-muted-foreground"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 mr-1" />
              Clear Mock Location
            </Button>
          )}
        </div>
      )}

      {/* Header / Toggle (sits at bottom; panel expands upward) */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-lg order-2"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4 text-blue-500" />
          <span>Mock Location</span>
        </div>
        {isExpanded ? (
          <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4" />
        ) : (
          <FontAwesomeIcon icon={faChevronUp} className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}
